import CloseButton from '@components/CloseButton'
import { Dialog, DialogHeader, LoadingDialog } from '@components/DewiDialog'
import { Avatar, Box } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useProposal } from '@helium/modular-governance-hooks'
import { PublicKey } from '@solana/web3.js'
import { useVotes } from '@hooks/useVotes'
import { ellipsizeAddress } from '@utils/solanaUtils'
import { useTranslation } from 'react-i18next'
import { useSolana } from '@dewicats/connect-button'

type VotesDialogProps = {
  proposalKey: string
  open: boolean
  onClose: () => void
}

const VotesDialog = ({ proposalKey, open, onClose }: VotesDialogProps) => {
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const onCloseHeader = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          padding: '0',
        },
      }}
    >
      <>
        <DialogHeader
          sx={{
            padding: '1.375rem',
          }}
        >
          <Box alignSelf="flex-end">
            <CloseButton onClick={onCloseHeader} />
          </Box>
        </DialogHeader>
        <VoteList proposalKey={proposalKey} />
      </>
    </Dialog>
  )
}

export const VoteList = ({ proposalKey }: { proposalKey: string }) => {
  const { t } = useTranslation()
  const { loading, markers } = useVotes(new PublicKey(proposalKey))
  const proposal = useProposal(new PublicKey(proposalKey))
  const { anchorProvider } = useSolana()

  const isDevnet = useMemo(() => {
    return anchorProvider?.connection.rpcEndpoint.includes('devnet')
  }, [anchorProvider])

  return (
    <Box
      sx={{
        padding: '1.375rem',
        overflow: 'overlay',
      }}
    >
      {loading && <LoadingDialog title="Loading" />}

      {!loading && (
        <table className="table-auto">
          <thead>
            <tr>
              <th className="text-start pb-4 text-gray-4 font-space font-bold">
                {t('allVotesDialog.title')}
              </th>
              <th className="text-start pb-4 text-gray-4 font-space font-bold">
                {t('allVotesDialog.subtitle')}
              </th>
            </tr>
          </thead>
          <tbody>
            {markers?.map((vote: any) => (
              <tr key={vote.mint.toBase58()}>
                <td className="flex items-center gap-2 pb-4 pr-4">
                  <Avatar
                    key={vote.image}
                    src={`${isDevnet ? '' : 'https://img-cdn.magiceden.dev/rs:fit:640:0:0/plain/'}${vote.image}`}
                  />
                  <a
                    target="_blank"
                    className="underline underline-offset-2 cursor-pointer text-indigo-5"
                    href={`https://explorer.solana.com/address/${vote.voter?.toBase58()}`}
                  >
                    {ellipsizeAddress(vote.voter?.toBase58(), {
                      numChars: 6,
                    })}
                  </a>
                </td>
                <td className="pb-4 pr-4">
                  {proposal?.info?.choices[vote?.choices?.[0]].name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Box>
  )
}

export default VotesDialog
